/**
 * Authentication context for managing user session state.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import api, { userApi } from '../services/api';
import { User } from '../types';

// Backend URL for auth redirects
const BACKEND_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  checkAuth: () => Promise<void>;
  logout: () => void;
  loginUrl: string;
  updateTimezone: (timezone: string) => Promise<void>;
  completeOnboarding: (timezone: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loginUrl = `${BACKEND_URL}/login/`;

  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    // Redirect to Django logout which will clear the session
    window.location.href = `${BACKEND_URL}/logout/`;
  }, []);

  const updateTimezone = useCallback(async (timezone: string) => {
    try {
      const response = await userApi.updateProfile({ timezone });
      setUser(response.data);
    } catch {
      throw new Error('Failed to update timezone');
    }
  }, []);

  const completeOnboarding = useCallback(async (timezone: string) => {
    try {
      const response = await userApi.updateProfile({ timezone, is_onboarded: true });
      setUser(response.data);
    } catch {
      throw new Error('Failed to complete onboarding');
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    checkAuth,
    logout,
    loginUrl,
    updateTimezone,
    completeOnboarding
  }), [user, loading, error, checkAuth, logout, loginUrl, updateTimezone, completeOnboarding]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
